import ko from "knockout";
import _ from "lodash";
import embeddingService from "services/embeddingService";
import PubSub from "util/PubSub";
import "extensions/i18n/knockout.i18n";
import localeService from "./localeService";

// ===================================================================================
// Constants
// ===================================================================================

const DELAY_SHORT = 5000;
const DELAY_LONG = 30000;

/**
 * Severity level enum
 * @readonly
 * @enum {string}
 */
const SEVERITY_LEVEL = {
    CRITICAL: "critical",
    MAJOR: "major",
    MINOR: "minor",
    INFO: "info",
};

// corresponds to methods in EPC notification service
// https://bitbucket.org/inindca/ember-purecloud-components/src/050b16b0604249b8b4c7f5e2d849a45f0b0f4350/addon/services/notification.js#notification.js-27
const TOAST_TYPE = {
    INFO: "info",
    ERROR: "error",
    SUCCESS: "success",
};

const SEVERITY_LEVEL_TO_TYPE = {
    [SEVERITY_LEVEL.CRITICAL]: TOAST_TYPE.ERROR,
    [SEVERITY_LEVEL.MAJOR]: TOAST_TYPE.ERROR,
    [SEVERITY_LEVEL.MINOR]: TOAST_TYPE.INFO,
    [SEVERITY_LEVEL.INFO]: TOAST_TYPE.INFO,
};

// ===================================================================================
// Private functions
// ===================================================================================

/**
 * Method to insert the toast into the singleton's list of toasts to be displayed in the UI
 * @param  {String} titleMessage - The message or message key of the string to display as the
 *  title of the notification.
 * @param  {SEVERITY_LEVEL} severity - the level of severity to attach to the toast
 * @param  {Object} options={}
 * @param  {String} [options.bodyMessageKey] - The message key of the
 *  string to display in the body of the notification.
 * @param {Object} [options.messageData=null] - An object containing key-value pairs that
 *  will be used to provide data to template keys in the titleMessage or
 *  bodyMessageKey arguments.
 * @param {Object} [options.bodyMessage=null] - The string to display in the body of the
 *  notification without localization. Overrides [bodyMessageKey].
 * @param {String|Date|Moment} options.timestamp - The timestamp to
 *  display in the notification to indicate when the notification occurred.
 * @param {String} options.icon - the icon class to display in the toast
 * @param {Integer} [options.delay] - the number of milliseconds to delay the notification before
 *  it self dismisses (null to not self dismiss)
 * @param {Object} [options.publishOnUserInteraction=null] - An object containing metadata
 *  that will be used to publish data to subscribers when a user clicks on a
 *  message notification. The object is required to have the following attributes:
 *      * topic: {String} A string key that will be used as the topic to publish the
 *         event data to.
 *      * data: {Object} A value of any type that will be sent as the argument to
 *         subscriber's of the topic name.
 */

const addToast = function (titleMessage, severity, options) {
    options = options || {};
    const locale = localeService.getAppLocale();

    const toast = {
        title: ko.i18n.keyExists(titleMessage)
            ? ko.i18n.renderString(titleMessage, options.messageData, locale)
            : titleMessage,
        publishOnUserInteraction: options.publishOnUserInteraction,
        type: SEVERITY_LEVEL_TO_TYPE[severity] || TOAST_TYPE.INFO,
    };

    if (options.bodyMessageKey && ko.i18n.keyExists(options.bodyMessageKey)) {
        toast.message = ko.i18n.renderString(
            options.bodyMessageKey,
            options.messageData,
            locale
        );
    } else if (options.bodyMessageKey) {
        toast.message = options.bodyMessageKey;
    } else if (ko.i18n.keyExists(options.bodyMessage)) {
        toast.message = ko.i18n.renderString(
            options.bodyMessage,
            options.messageData,
            locale
        );
    } else {
        toast.message = options.bodyMessage || "";
    }

    if (toast.message === "") {
        toast.message = toast.title;
        toast.title = "";
    }

    if (options.tag) {
        toast.tag = options.tag;
    }

    // The following regex will replace matches with 'href=#/engage/'
    //
    // Examples of what will be matched:
    //      href   =   '#queues/....'
    //      href="#agents/...."
    //
    // The following will be left alone
    //      href = "#/engage/......"
    //

    toast.message = toast.message.replace(
        /href\s*=\s*["']#(?!\/engage)/gi,
        'href="#/engage/'
    );

    //Engage toasts use milliseconds, web-directory toasts use seconds
    toast.hideAfter = options.delay ? options.delay / 1000 : false;

    // Prevent markdown rendering in toast
    toast.markdownToHtml = false;

    embeddingService.sendToastNotification(toast);
};

// ===================================================================================
// Class definition
// ===================================================================================

const ToastService = function () {
    const self = this;

    self.DELAY_SHORT = DELAY_SHORT;
    self.DELAY_LONG = DELAY_LONG;

    /**
     * Notify the user of a generic event that they may be interested in. This message
     * should be used to let the user know about new informat, updated information, or
     * general status of an item in the system.
     *
     * These alerts will disappear after ~5 seconds.
     *
     * @param  {String} titleMessage - The message or message key of the string to display as the
     *  title of the notification.
     * @param  {Object} [options={}]
     * @param  {String} [options.icon="fa-info-circle"] - The name of the CSS icon to
     *  display.
     * @param  {String} [options.bodyMessageKey=null] - The message key of the
     *  string to display in the body of the notification.
     * @param {Object} [options.messageData=null] - An object containing key-value pairs that
     *  will be used to provide data to template keys in the titleMessage or
     *  bodyMessageKey arguments.
     * @param {Object} [options.bodyMessage=null] - The string to display in the body of the
     *  notification without localization. Overrides [bodyMessageKey].
     * @param {String|Date|Moment} [options.timestamp=undefined] - The timestamp to
     *  display in the notification to indicate when the notification occurred.
     * @param {Object} [options.publishOnUserInteraction=null] - An object containing metadata
     *  that will be used to publish data to subscribers when a user clicks on a
     *  message notification. The object is required to have the following attributes:
     *      * topic: {String} A string key that will be used as the topic to publish the
     *         event data to.
     *      * data: {Object} A value of any type that will be sent as the argument to
     *         subscriber's of the topic name.
     */
    self.notifyMessage = function (titleMessage, options, useLongDelay) {
        const delay = useLongDelay ? DELAY_LONG : DELAY_SHORT;

        const opts = _.assign(
            {
                bodyMessageKey: null,
                icon: "fa-info-circle",
                timestamp: null,
                messageData: null,
                publishOnUserInteraction: null,
                bodyMessage: null,
            },
            options,
            {
                delay: delay,
            }
        );

        if (
            !_.isNull(opts.publishOnUserInteraction) &&
            (!_.has(opts.publishOnUserInteraction, "topic") ||
                !_.has(opts.publishOnUserInteraction, "data"))
        ) {
            throw new Error(
                "notifications.notifyMessage(): The value for publishOnUserInteraction " +
                    "is required to be a plain object that contains a 'topic' attribute " +
                    "and 'data' attribute."
            );
        }

        addToast(titleMessage, SEVERITY_LEVEL.INFO, opts);
    };

    /**
     * Notify the user of an alert that has a "minor" severity level. These alerts
     * should be used for informational purposes about events in the system.
     *
     * These alerts are considered sticky and will not disappear until the user
     * explicitly closes them.
     *
     * @param  {String} titleMessage - The message or message key of the string to display as the
     *  title of the notification.
     * @param  {Object} [options={}]
     * @param  {String} [options.bodyMessageKey=null] - The message key of the
     *  string to display in the body of the notification.
     * @param {Object} [options.messageData=null] - An object containing key-value pairs that
     *  will be used to provide data to template keys in the titleMessage or
     *  bodyMessageKey arguments.
     * @param {Object} [options.bodyMessage=null] - The string to display in the body of the
     *  notification without localization. Overrides [bodyMessageKey].
     * @param {String|Date|Moment} [options.timestamp=undefined] - The timestamp to
     *  display in the notification to indicate when the notification occurred.
     */
    self.notifyMinorAlert = function (titleMessage, options) {
        const opts = _.assign(
            {
                bodyMessageKey: null,
                messageData: null,
                timestamp: null,
                publishOnUserInteraction: null,
                bodyMessage: null,
            },
            options,
            {
                icon: "fa-exclamation-circle",
                delay: DELAY_SHORT,
            }
        );

        addToast(titleMessage, SEVERITY_LEVEL.MINOR, opts);
    };

    /**
     * Notify the user of an alert that has a "major" severity level. These alerts
     * should be used to alert the user of events that they wish to know about and
     * may act upon. For example, a metric that has risen above a certain threshold
     * in the system.
     *
     * These alerts are considered sticky and will not disappear until the user
     * explicitly closes them.
     *
     * @param  {String} titleMessage - The message or message key of the string to display as the
     *  title of the notification.
     * @param  {Object} [options={}]
     * @param  {String} [options.bodyMessageKey=null] - The message key of the
     *  string to display in the body of the notification.
     * @param {Object} [options.messageData=null] - An object containing key-value pairs that
     *  will be used to provide data to template keys in the titleMessage or
     *  bodyMessageKey arguments.
     * @param {Object} [options.bodyMessage=null] - The string to display in the body of the
     *  notification without localization. Overrides [bodyMessageKey].
     * @param {String|Date|Moment} [options.timestamp=undefined] - The timestamp to
     *  display in the notification to indicate when the notification occurred.
     */
    self.notifyMajorAlert = function (titleMessage, options) {
        const opts = _.assign(
            {
                bodyMessageKey: null,
                messageData: null,
                timestamp: null,
                publishOnUserInteraction: null,
                bodyMessage: null,
            },
            options,
            {
                icon: "fa-exclamation-triangle",
                delay: null,
            }
        );

        addToast(titleMessage, SEVERITY_LEVEL.MAJOR, opts);
    };

    /**
     * Notify the user of an alert that has a "critical" severity level. These alerts
     * should be used to alert the user of events that cause functional or usability
     * issues in the application, e.g. a connectivity loss. These error may also be
     * used to trigger threshold violations to the user, e.g. an agent asking for
     * immediate attention.
     *
     * These alerts are considered sticky and will not disappear until the user
     * explicitly closes them.
     *
     * @param  {String} titleMessage - The message or message key of the string to display as the
     *  title of the notification.
     * @param  {Object} [options={}]
     * @param  {String} [options.bodyMessageKey=null] - The message key of the
     *  string to display in the body of the notification.
     * @param {Object} [options.messageData=null] - An object containing key-value pairs that
     *  will be used to provide data to template keys in the titleMessage or
     *  bodyMessageKey arguments.
     * @param {Object} [options.bodyMessage=null] - The string to display in the body of the
     *  notification without localization. Overrides [bodyMessageKey].
     * @param {String|Date|Moment} [options.timestamp=undefined] - The timestamp to
     *  display in the notification to indicate when the notification occurred.
     */
    self.notifyCriticalAlert = function (titleMessage, options) {
        const opts = _.assign(
            {
                bodyMessageKey: null,
                messageData: null,
                timestamp: null,
                publishOnUserInteraction: null,
                bodyMessage: null,
            },
            options,
            {
                icon: "fa-exclamation",
                delay: null,
            }
        );

        addToast(titleMessage, SEVERITY_LEVEL.CRITICAL, opts);
    };

    // ===============================================================================
    // User Interaction Events
    // ===============================================================================

    /**
     * Subscribe a callback that will execute when a user interacts with a
     * notification that has opted into publishing an event. An example of this would
     * be to subscribe to when a user clicks on a "new voicemail" message notification
     * and the communication inbox should open.
     *
     * @param  {String}   topicName - A topic key indicating the event to subscribe to.
     * @param  {Function} callback  - A function that will be executed when an event
     *  has been published to the subscribed topicName.
     */
    self.subscribeToUserInteraction = function (topicName, callback) {
        PubSub.Topic(topicName).subscribe(callback);
    };

    /**
     * Unsubscribe a callback from a topic name that receives events when a user
     * interacts with a notification.
     *
     * @param  {String}   topicName - A topic key indicating the event to unsubscribe
     *  from.
     * @param  {Function} callback  - A function that will be removed from the
     *  collection of subscribed callbacks for the given topic name.
     */
    self.unsubscribeFromUserInteraction = function (topicName, callback) {
        PubSub.Topic(topicName).unsubscribe(callback);
    };
};

// ===================================================================================
// Singleton class instantiation
// ===================================================================================

export default new ToastService();
